import { RefObject, useEffect } from 'react'
import { gsap } from '../gsap'

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

interface ScrollParallaxOptions {
  /** Vertical travel (px) while the section crosses the viewport */
  from: () => number
  to: () => number
  scale?: number
}

/**
 * Scrub-linked vertical parallax of `target` while `section` crosses the
 * viewport ('top bottom' → 'bottom top'). No-op under reduced motion.
 */
export const useScrollParallax = (
  section: RefObject<HTMLElement>,
  target: RefObject<HTMLElement>,
  { from, to, scale = 1 }: ScrollParallaxOptions,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const trigger = section.current
    const element = target.current
    if (!trigger || !element || prefersReducedMotion()) return undefined

    const tween = gsap.fromTo(
      element,
      { y: from, scale },
      {
        y: to,
        scale,
        ease: 'none',
        immediateRender: true,
        scrollTrigger: { trigger, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
      }
    )

    return () => {
      tween.scrollTrigger?.kill()
      tween.kill()
      gsap.set(element, { clearProps: 'transform' })
    }
  }, deps)
}

// Depth per decor kind (desktop px, halved on mobile): hops travel less
// than wheat so the layers separate while scrolling
const FLOAT_TRAVEL: Record<string, [number, number]> = {
  hop: [-120, 80],
  wheat: [-60, 140]
}

/**
 * Floating hops/wheat: every `[data-float="hop|wheat"]` wrapper inside
 * `section` gets a scroll-scrubbed vertical parallax, and the image inside
 * it an endless idle sway (6-12px, 2-4°, phase-shifted per element) — the
 * tecate.com cans feel. Two elements so both tweens own their own `y`.
 * Reduced motion disables everything.
 */
export const useFloatingDecor = (section: RefObject<HTMLElement>, isDesktop: boolean, deps: unknown[] = []) => {
  useEffect(() => {
    const root = section.current
    if (!root || prefersReducedMotion()) return undefined

    const floats = Array.from(root.querySelectorAll<HTMLElement>('[data-float]'))
    const factor = isDesktop ? 1 : 0.5
    const targets: HTMLElement[] = []
    const tweens = floats.flatMap((wrapper, index) => {
      const [from, to] = FLOAT_TRAVEL[wrapper.dataset.float ?? 'hop'] ?? FLOAT_TRAVEL.hop
      const inner = (wrapper.firstElementChild as HTMLElement | null) ?? wrapper
      targets.push(wrapper, inner)
      const parallax = gsap.fromTo(
        wrapper,
        { y: from * factor },
        {
          y: to * factor,
          ease: 'none',
          immediateRender: true,
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
        }
      )
      const amplitude = 6 + (index % 4) * 2
      const rotation = 2 + (index % 3)
      const sway = gsap.fromTo(
        inner,
        { y: -amplitude, rotation: -rotation },
        {
          y: amplitude,
          rotation,
          duration: 3 + (index % 3),
          delay: index * 0.45,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        }
      )
      return [parallax, sway]
    })

    return () => {
      tweens.forEach((tween) => {
        tween.scrollTrigger?.kill()
        tween.kill()
      })
      targets.forEach((element) => gsap.set(element, { clearProps: 'transform' }))
    }
  }, [isDesktop, ...deps])
}
